import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import EnderecoFields from '../components/EnderecoFields';
import { ENDERECO_VAZIO } from '../utils/cep';

const FORM_VAZIO = {
  nomeOficina: '', cnpj: '', telefone: '', ...ENDERECO_VAZIO,
  proprietarioMecanico: false,
  adminNome: '', adminEmail: '', adminSenha: '', proprietarioTelefone: '',
};

const SENHA_PADRAO_PROPRIETARIO = 'mecanico@123';

export default function Oficinas() {
  const { usuario, logout } = useAuth();
  const navigate = useNavigate();
  const [oficinas, setOficinas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editando, setEditando] = useState(null); // oficina em edição, ou null = criando
  const [form, setForm] = useState(FORM_VAZIO);
  const [novaSenhaAdmin, setNovaSenhaAdmin] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [tema, setTema] = useState(() => localStorage.getItem('tema-superadmin') || 'light');

  useEffect(() => {
    document.documentElement.classList.toggle('dark', tema === 'dark');
    localStorage.setItem('tema-superadmin', tema);
    // Nenhuma outra tela do app usa `dark:` — some a classe ao sair daqui para
    // não vazar num estado visual sem efeito nas telas de loja.
    return () => document.documentElement.classList.remove('dark');
  }, [tema]);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/oficinas');
      setOficinas(data);
    } catch { toast.error('Erro ao carregar oficinas'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const handleLogout = () => { logout(); navigate('/login'); };

  const abrirCriar = () => { setEditando(null); setForm(FORM_VAZIO); setNovaSenhaAdmin(''); setModal(true); };

  const abrirEditar = of => {
    setEditando(of);
    setForm({
      nomeOficina: of.nomeOficina || '', cnpj: of.cnpj || '', telefone: of.telefone || '',
      cep: of.cep || '', endereco: of.endereco || '', numero: of.numero || '', bairro: of.bairro || '', cidade: of.cidade || '', estado: of.estado || '',
      adminNome: '', adminEmail: '', adminSenha: '',
    });
    setNovaSenhaAdmin('');
    setModal(true);
  };

  const salvar = async e => {
    e.preventDefault();
    if (editando) {
      setSalvando(true);
      try {
        const { nomeOficina, cnpj, telefone, cep, endereco, numero, bairro, cidade, estado } = form;
        await api.put(`/oficinas/${editando._id}`, { nomeOficina, cnpj, telefone, cep, endereco, numero, bairro, cidade, estado });
        if (novaSenhaAdmin) {
          await api.put(`/oficinas/${editando._id}/admin-senha`, { senha: novaSenhaAdmin });
        }
        toast.success('Oficina atualizada!');
        setModal(false);
        carregar();
      } catch (err) { toast.error(err.response?.data?.erro || 'Erro ao atualizar oficina'); }
      finally { setSalvando(false); }
      return;
    }
    if (!form.nomeOficina || !form.cnpj) {
      return toast.error('Preencha o nome da oficina e o CNPJ');
    }
    if (form.proprietarioMecanico) {
      if (!form.adminNome || !form.proprietarioTelefone) {
        return toast.error('Preencha o nome e o telefone do proprietário');
      }
    } else if (!form.adminNome || !form.adminEmail || !form.adminSenha) {
      return toast.error('Preencha o nome da oficina, o CNPJ e os dados do admin');
    }
    setSalvando(true);
    try {
      await api.post('/oficinas', form);
      toast.success('Oficina criada!');
      setModal(false);
      setForm(FORM_VAZIO);
      carregar();
    } catch (err) { toast.error(err.response?.data?.erro || 'Erro ao criar oficina'); }
    finally { setSalvando(false); }
  };

  const toggleAtiva = async of => {
    try {
      await api.put(`/oficinas/${of._id}`, { ativa: !of.ativa });
      toast.success(of.ativa ? 'Oficina desativada' : 'Oficina ativada');
      carregar();
    } catch { toast.error('Erro ao atualizar'); }
  };

  const remover = async of => {
    if (!window.confirm(`Remover "${of.nomeOficina}" e TODOS os seus dados? Esta ação é irreversível.`)) return;
    try {
      await api.delete(`/oficinas/${of._id}`);
      toast.success('Oficina removida');
      carregar();
    } catch { toast.error('Erro ao remover'); }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      {/* Header de plataforma */}
      <header className="bg-gray-900 dark:bg-black text-white shadow-lg">
        <div className="flex items-center justify-between px-4 py-3 max-w-5xl mx-auto w-full">
          <span className="font-bold text-lg">🏢 Plataforma — Oficinas</span>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-300 hidden sm:inline">{usuario?.nome}</span>
            <button onClick={() => setTema(t => (t === 'dark' ? 'light' : 'dark'))}
              title={tema === 'dark' ? 'Tema claro' : 'Tema escuro'}
              className="text-sm bg-gray-700 hover:bg-gray-600 px-3 py-1.5 rounded-lg transition-colors">
              {tema === 'dark' ? '☀️' : '🌙'}
            </button>
            <button onClick={handleLogout} className="text-sm bg-gray-700 hover:bg-gray-600 px-3 py-1.5 rounded-lg transition-colors">Sair</button>
          </div>
        </div>
      </header>

      <main className="flex-1 p-4 max-w-5xl mx-auto w-full">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Oficinas</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Gerencie as oficinas clientes da plataforma.</p>
          </div>
          <button onClick={abrirCriar} className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors">
            + Nova Oficina
          </button>
        </div>

        {loading ? (
          <div className="text-center py-16 text-gray-400 dark:text-gray-500">Carregando...</div>
        ) : oficinas.length === 0 ? (
          <div className="text-center py-16 text-gray-400 dark:text-gray-500">
            <div className="text-5xl mb-3">🏢</div>
            <p>Nenhuma oficina cadastrada ainda.</p>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {oficinas.map(of => (
              <div key={of._id} className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-4 ${of.ativa ? 'border-gray-100 dark:border-gray-700' : 'border-l-4 border-l-gray-300 dark:border-l-gray-600 border-gray-100 dark:border-gray-700 opacity-75'}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-800 dark:text-gray-100 truncate">{of.nomeOficina}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${of.ativa ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
                        {of.ativa ? 'Ativa' : 'Inativa'}
                      </span>
                    </div>
                    {of.cidade && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{of.cidade}</p>}
                    {of.adminEmail && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">✉️ {of.adminEmail}</p>}
                  </div>
                </div>
                <div className="flex gap-4 text-xs text-gray-500 dark:text-gray-400 mb-3">
                  <span>👤 {of._count?.usuarios ?? 0} usuário(s)</span>
                  <span>🚗 {of._count?.clientes ?? 0} cliente(s)</span>
                  <span>🔧 {of._count?.servicos ?? 0} OS</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => abrirEditar(of)} className="flex-1 border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 py-1.5 rounded-lg text-xs hover:bg-gray-50 dark:hover:bg-gray-700">
                    Editar
                  </button>
                  <button onClick={() => toggleAtiva(of)} className="flex-1 border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 py-1.5 rounded-lg text-xs hover:bg-gray-50 dark:hover:bg-gray-700">
                    {of.ativa ? 'Desativar' : 'Ativar'}
                  </button>
                  <button onClick={() => remover(of)} className="text-red-500 dark:text-red-400 px-3 py-1.5 rounded-lg text-xs hover:bg-red-50 dark:hover:bg-red-900/30">Remover</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Modal nova oficina */}
      {modal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-lg shadow-xl max-h-[93vh] flex flex-col">
            <div className="p-5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
              <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">{editando ? `Editar Oficina — ${editando.nomeOficina}` : 'Nova Oficina'}</h2>
              <button type="button" onClick={() => setModal(false)} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 text-2xl leading-none">×</button>
            </div>
            <form onSubmit={salvar} className="overflow-y-auto flex-1 flex flex-col">
              <div className="p-5 space-y-4">
                <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Dados da Oficina</p>
                {[
                  { key: 'nomeOficina', label: 'Nome da oficina *', required: true },
                  { key: 'cnpj', label: 'CNPJ *', required: true },
                  { key: 'telefone', label: 'Telefone' },
                ].map(f => (
                  <div key={f.key}>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{f.label}</label>
                    <input required={f.required}
                      className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} />
                  </div>
                ))}
                <EnderecoFields form={form} setForm={setForm} />

                {editando ? (
                  <>
                    <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide pt-2">
                      {editando.adminPerfil === 'proprietario' ? 'Proprietário/Mecânico' : 'Administrador'}
                    </p>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {editando.adminPerfil === 'proprietario' ? 'Telefone (login)' : 'E-mail (login)'}
                      </label>
                      <input type="text" disabled value={editando.adminEmail || '—'}
                        className="w-full border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-lg px-3 py-2.5 text-base cursor-not-allowed" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nova senha</label>
                      <input type="password" placeholder="Deixe em branco para não alterar"
                        className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={novaSenhaAdmin} onChange={e => setNovaSenhaAdmin(e.target.value)} />
                    </div>
                  </>
                ) : (
                  <>
                    <label className="flex items-center gap-2 pt-2 cursor-pointer select-none">
                      <input type="checkbox"
                        checked={form.proprietarioMecanico}
                        onChange={e => setForm(p => ({ ...p, proprietarioMecanico: e.target.checked }))}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">O proprietário também é o mecânico?</span>
                    </label>

                    {form.proprietarioMecanico ? (
                      <>
                        <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide pt-2">Proprietário/Mecânico</p>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nome *</label>
                          <input type="text" required
                            className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={form.adminNome} onChange={e => setForm(p => ({ ...p, adminNome: e.target.value }))} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Telefone (login) *</label>
                          <input type="tel" required placeholder="(00) 00000-0000"
                            className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={form.proprietarioTelefone} onChange={e => setForm(p => ({ ...p, proprietarioTelefone: e.target.value }))} />
                        </div>
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                          Senha inicial: <span className="font-mono">{SENHA_PADRAO_PROPRIETARIO}</span> — será exigida a troca no primeiro acesso.
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide pt-2">Administrador inicial</p>
                        {[
                          { key: 'adminNome', label: 'Nome do admin *', type: 'text', required: true },
                          { key: 'adminEmail', label: 'E-mail (login) *', type: 'email', required: true },
                          { key: 'adminSenha', label: 'Senha *', type: 'password', required: true },
                        ].map(f => (
                          <div key={f.key}>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{f.label}</label>
                            <input type={f.type} required={f.required}
                              className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                              value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} />
                          </div>
                        ))}
                      </>
                    )}
                  </>
                )}
              </div>
              <div className="flex gap-3 p-5 border-t border-gray-100 dark:border-gray-700 flex-shrink-0 mt-auto">
                <button type="button" onClick={() => setModal(false)} className="flex-1 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 py-2.5 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700">Cancelar</button>
                <button type="submit" disabled={salvando} className="flex-1 bg-blue-600 text-white py-2.5 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
                  {salvando ? 'Salvando...' : editando ? 'Salvar Alterações' : 'Criar Oficina'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
